// frontend/src/hooks/useConfirmation.js
import { useState, useCallback } from 'react';

/**
 * 🪝 HOOK PARA GERENCIAR MODAIS DE CONFIRMAÇÃO
 * 
 * Este hook simplifica o uso de modais de confirmação,
 * fornecendo uma API limpa e reutilizável.
 */

export const useConfirmation = () => {
  const [confirmationState, setConfirmationState] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    type: 'danger',
    icon: null,
    requireTyping: false,
    confirmationText: '',
    showCheckbox: false,
    checkboxText: '',
    additionalInfo: null,
    onConfirm: null,
    size: 'md',
    loading: false
  });

  // Mostrar modal de confirmação
  const showConfirmation = useCallback((options) => {
    const {
      title = 'Confirmar Ação',
      message = 'Tem certeza que deseja continuar?',
      confirmText = 'Confirmar',
      cancelText = 'Cancelar',
      type = 'danger',
      icon = null,
      requireTyping = false,
      confirmationText = '',
      showCheckbox = false,
      checkboxText = 'Compreendo que esta ação não pode ser desfeita',
      additionalInfo = null,
      size = 'md',
      onConfirm
    } = options;

    return new Promise((resolve, reject) => {
      setConfirmationState({
        show: true,
        title,
        message,
        confirmText,
        cancelText,
        type,
        icon,
        requireTyping,
        confirmationText,
        showCheckbox,
        checkboxText,
        additionalInfo,
        size,
        loading: false,
        onConfirm: async () => {
          try {
            setConfirmationState(prev => ({ ...prev, loading: true }));
            
            if (onConfirm) {
              const result = await onConfirm();
              resolve(result);
            } else {
              resolve(true);
            }
          } catch (error) {
            reject(error);
          } finally {
            setConfirmationState(prev => ({ ...prev, loading: false }));
          }
        }
      });
    });
  }, []);

  // Esconder modal
  const hideConfirmation = useCallback(() => {
    setConfirmationState(prev => ({ ...prev, show: false, loading: false }));
  }, []);

  // Métodos de conveniência para tipos específicos
  const confirmDelete = useCallback((itemName, onConfirm) => {
    return showConfirmation({
      title: 'Confirmar Exclusão',
      message: `Tem certeza que deseja excluir "${itemName}"?`,
      confirmText: 'Excluir',
      type: 'danger',
      icon: 'mdi:delete',
      showCheckbox: true,
      checkboxText: 'Compreendo que esta ação não pode ser desfeita',
      onConfirm
    });
  }, [showConfirmation]);

  const confirmDeletePermanent = useCallback((itemName, onConfirm) => {
    return showConfirmation({
      title: 'Exclusão Permanente',
      message: `Esta ação irá excluir permanentemente "${itemName}" e não poderá ser desfeita.`,
      confirmText: 'Excluir Permanentemente',
      type: 'danger',
      icon: 'mdi:delete-forever',
      requireTyping: true,
      confirmationText: 'EXCLUIR',
      showCheckbox: true,
      checkboxText: 'Compreendo que esta ação é irreversível',
      additionalInfo: (
        <div className="text-danger">
          <strong>⚠️ Atenção:</strong> Esta ação não pode ser desfeita!
        </div>
      ),
      onConfirm
    });
  }, [showConfirmation]);

  const confirmAction = useCallback((title, message, onConfirm, options = {}) => {
    return showConfirmation({
      title,
      message,
      type: 'warning',
      icon: 'mdi:alert',
      ...options,
      onConfirm
    });
  }, [showConfirmation]);

  const confirmDeactivate = useCallback((itemName, onConfirm) => {
    return showConfirmation({
      title: 'Confirmar Desativação',
      message: `Deseja desativar "${itemName}"?`,
      confirmText: 'Desativar',
      type: 'warning',
      icon: 'mdi:pause-circle',
      additionalInfo: (
        <div className="text-muted">
          <small>Esta ação pode ser revertida posteriormente.</small>
        </div>
      ),
      onConfirm
    });
  }, [showConfirmation]);

  const confirmActivate = useCallback((itemName, onConfirm) => {
    return showConfirmation({
      title: 'Confirmar Ativação',
      message: `Deseja ativar "${itemName}"?`,
      confirmText: 'Ativar',
      type: 'success',
      icon: 'mdi:play-circle',
      onConfirm
    });
  }, [showConfirmation]);

  const confirmBulkAction = useCallback((action, itemCount, onConfirm) => {
    return showConfirmation({
      title: `Confirmar ${action}`,
      message: `Esta ação afetará ${itemCount} ${itemCount === 1 ? 'item' : 'itens'}.`,
      confirmText: 'Continuar',
      type: 'warning',
      icon: 'mdi:format-list-bulleted',
      showCheckbox: itemCount > 5,
      checkboxText: `Compreendo que esta ação afetará ${itemCount} itens`,
      onConfirm
    });
  }, [showConfirmation]);

  const confirmSensitiveAction = useCallback((title, message, confirmationWord, onConfirm) => {
    return showConfirmation({
      title,
      message,
      confirmText: 'Executar',
      type: 'danger',
      icon: 'mdi:shield-alert',
      requireTyping: true,
      confirmationText: confirmationWord,
      showCheckbox: true,
      checkboxText: 'Compreendo os riscos desta ação',
      onConfirm
    });
  }, [showConfirmation]);

  // Estado para componente ConfirmationModal
  const modalProps = {
    ...confirmationState,
    onHide: hideConfirmation,
    onConfirm: confirmationState.onConfirm
  };

  return {
    // Estado do modal
    modalProps,
    
    // Controles básicos
    showConfirmation,
    hideConfirmation,
    
    // Métodos de conveniência
    confirmDelete,
    confirmDeletePermanent,
    confirmAction,
    confirmDeactivate,
    confirmActivate,
    confirmBulkAction,
    confirmSensitiveAction
  };
};



























